import React from "react";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export default function BulkActionBar({
  selectedCount,
  onClearSelection,
  onAction,
  actionLabel = "Mover",
  options,
  itemType = "itens",
  placeholder = "Selecione status...",
  // New props for action types
  actionTypes = null,
  actionType = null,
  onActionTypeChange = null,
  // New prop for direct actions (no dropdown needed)
  directActionTypes = [],
}) {
  const [selectedOption, setSelectedOption] = React.useState("");

  // Reset selected option when action type changes
  React.useEffect(() => {
    setSelectedOption("");
  }, [actionType]);

  const handleAction = () => {
    if (selectedOption && onAction) {
      onAction(selectedOption);
      setSelectedOption("");
    }
  };

  if (selectedCount === 0) return null;

  // Determine if we're using multi-action mode
  const hasActionTypes = actionTypes && actionTypes.length > 0 && onActionTypeChange;
  
  // Check if current action type is a direct action (no options dropdown needed)
  const isDirectAction = directActionTypes.includes(actionType);

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 bg-primary text-primary-foreground shadow-2xl border-t-4 border-primary-foreground/20"
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary-foreground/20 rounded-full w-8 h-8 flex items-center justify-center">
              <span className="text-sm font-bold">{selectedCount}</span>
            </div>
            <span className="font-semibold">
              {selectedCount} {itemType} selecionado{selectedCount > 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="hover:bg-primary-foreground/20 text-primary-foreground"
            >
              <X className="w-4 h-4 mr-2" />
              Limpar
            </Button>

            {/* Action Type Selector (new) */}
            {hasActionTypes && (
              <Select value={actionType} onValueChange={onActionTypeChange}>
                <SelectTrigger className="w-full md:w-[160px] bg-primary-foreground text-primary border-none">
                  <SelectValue placeholder="Tipo de ação..." />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  {actionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Options Selector - Hide for direct actions */}
            {!isDirectAction && (
              <Select value={selectedOption} onValueChange={setSelectedOption}>
                <SelectTrigger className="w-full md:w-[200px] bg-primary-foreground text-primary border-none">
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent className="bg-background max-h-[300px]">
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              onClick={isDirectAction ? () => onAction(actionType) : handleAction}
              disabled={!isDirectAction && !selectedOption}
              className={cn(
                "font-semibold",
                actionType === 'delete' 
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              )}
            >
              {actionLabel}
              {actionType === 'delete' ? <Trash2 className="w-4 h-4 ml-2" /> : <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}